import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getScoreClass, getScoreLabel } from '@/utils/scoreUtils';
import { IonIcon, IonSpinner } from '@ionic/react';
import { add } from 'ionicons/icons';
import { supabase } from '@/utils/supabase';
import { api } from '@/utils/api';

interface HoleData {
  hole: number;
  par: number;
  handicap: number;
}

interface PlayerScoreData {
  hole: number;
  score: number | null;
  putts: number | null;
}

interface GameScore {
  [holeNumber: string]: {
    score: number | null;
    putts: number | null;
  };
}

interface Player {
  userId: string;
  name: string;
  scores: GameScore;
  status: string;
}

interface HoleSpecificData {
  par: number;
  handicap: number;
}

interface InGameScorecardProps {
  gameId: string;
  playerId: string;
  playerName: string;
}

const InGameScorecard: React.FC<InGameScorecardProps> = ({ gameId, playerId, playerName }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [holeData, setHoleData] = useState<HoleData[]>([]);
  const [playerScores, setPlayerScores] = useState<PlayerScoreData[]>([]);

  // Fetch game data and subscribe to changes
  useEffect(() => {
    let subscription: any;

    const fetchGameData = async () => {
      try {
        setLoading(true);

        // Fetch game details to get hole data
        const { data: gameData } = await api.get(`/game/${gameId}`);

        if (gameData) {
          // Set hole data
          const holes = gameData.holes || [];
          const formattedHoleData = holes.map((hole: HoleSpecificData, index: number) => ({
            hole: index + 1,
            par: hole.par || 0,
            handicap: hole.handicap || 0
          }));

          setHoleData(formattedHoleData);

          // Find player in players array
          const player = gameData.players.find((p: Player) => p.userId === playerId);

          if (player) {
            // Format player scores from the gameData.scores structure
            const formattedScores = formattedHoleData.map((hole: HoleData) => {
              // Access scores from the correct structure: gameData.scores[holeNumber][playerId]
              const holeScores = gameData.scores?.[hole.hole]?.[playerId];
              
              return {
                hole: hole.hole,
                score: holeScores?.score ?? null,
                putts: holeScores?.putts ?? null
              };
            });

            setPlayerScores(formattedScores);
          }
        }
      } catch (error) {
        console.error('Error fetching game data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchGameData();

    // Subscribe to game score changes
    subscription = supabase
      .channel(`game_score_${gameId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_score',
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          // Refetch data when scores change
          fetchGameData();
        }
      )
      .subscribe();

    return () => {
      // Clean up subscription
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [gameId, playerId]);

  // Calculate totals
  const totalPar = holeData.reduce((sum, hole) => sum + hole.par, 0);
  const totalScore = playerScores
    .filter(score => score.score !== null)
    .reduce((sum, score) => sum + (score.score || 0), 0);

  const completedHoles = playerScores.filter(score => score.score !== null).length;

  const getPlayerScoreForHole = (holeNumber: number) => {
    const scoreData = playerScores.find(ps => ps.hole === holeNumber);
    return scoreData ? scoreData.score : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <IonSpinner name="dots" />
      </div>
    );
  }

  return (
    <div className="w-full mb-safe">
      <div className="grid grid-cols-4 gap-2 p-2 bg-secondary text-secondary-foreground font-bold rounded-t-lg text-center text-sm">
        <div>{t('hole')}</div>
        <div>{t('par')}</div>
        <div>{t('handicap')}</div>
        <div>{t('score')}</div>
      </div>
      <div className="divide-y divide-input border border-input rounded-b-lg">
        {holeData.map((holeInfo) => {
          const playerScore = getPlayerScoreForHole(holeInfo.hole);
          const par = holeInfo.par;
          const scoreClass = playerScore !== null ? getScoreClass(playerScore, par) : '';

          // Display tap to enter for null scores
          const scoreContent = playerScore !== null ? (
            <div className={`font-bold ${scoreClass}`}>
              {getScoreLabel(playerScore, par, t)}
            </div>
          ) : (
            <div className="text-muted-foreground flex items-center justify-center">
              <IonIcon icon={add} className="mr-1 text-xs" />
              <span className="text-xs">{t('tap')}</span>
            </div>
          );

          return (
            <div key={`hole-${holeInfo.hole}`} className="grid grid-cols-4 gap-2 p-2 items-center text-center text-sm">
              <div className="font-semibold">{holeInfo.hole}</div>
              <div>{holeInfo.par}</div>
              <div>{holeInfo.handicap}</div>
              {scoreContent}
            </div>
          );
        })}
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-4 gap-2 p-2 bg-secondary text-secondary-foreground font-bold rounded-b-lg text-center text-sm mt-2">
        <div>{t('total')}</div>
        <div>{totalPar}</div>
        <div>-</div>
        <div className={completedHoles > 0 ? (totalScore > totalPar ? 'text-red-500' : 'text-green-500') : ''}>
          {completedHoles > 0 ? totalScore : '-'}
          {completedHoles > 0 && completedHoles < holeData.length && ` (${completedHoles}/${holeData.length})`}
        </div>
      </div>
    </div>
  );
};

export default InGameScorecard;
